import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { debounceTime } from 'rxjs';
import { PageableComponent } from '@shared/pageable.component';
import { ResolutionCenterService, ChatUser, AVAILABLE_REACTIONS, ClientChat, userDetailsToChatUser } from '../resolution-center.service';
import { getLastMessageAttachmentPreview } from '../chat-attachment.util';
import { UserService, UserTagList, UserTag } from '@app/user/user.service';

@Component({
    selector: 'client-center-mantis',
    templateUrl: './client-center-mantis.component.html',
    styleUrls: ['./client-center-mantis.component.scss'],
    standalone: false
})

export class ClientCenterMantisComponent extends PageableComponent<any> implements OnInit, OnDestroy {
    constructor(
        private route: ActivatedRoute,
        private resolutionCenterService: ResolutionCenterService,
        private userService: UserService
    ) {
        super();

        this.searchQueryStream.pipe(
            debounceTime(500)
        ).subscribe(() => (
            this._page = 1,
            this.load()
        ));
    }

    infoCollapsed = false; //mantis demo stuff

    get selectedChat() { return this._selectedChat; }
    set selectedChat(value: any) {
        if (value !== this._selectedChat) {
            this._selectedChat = value;
            //messages are now loaded by messages-tab component via getCustomerMessagesById
            //no need to call getContactMessages here
        }
    }

    getUser: any = null;

    // Reset trigger to force messages-tab to reset even when chat is already null
    public resetMessagesTabTrigger: number = 0;

    /** When set, messages-tab shows new-chat composer for this user (no existing chat). */
    public selectedUserForNewChat: ChatUser | null = null;

    public isLoading = true;
    public showLoadConversations = false;
    public showLoadMoreChats = false;
    public availableReactions: string[] = AVAILABLE_REACTIONS;
    public conversations: ClientChat[] = [];
    public chatStatus: '' | 'unread' | 'archived' | 'favorited' = '';
    public today = '';
    public yesterday = '';
    
    // Filter modal properties
    public userTagLists: UserTagList[] = [];
    public showFiltersModal = false;
    public selectedTags: number[] = []; // Array of selected tag IDs
    public tempSelectedTags: number[] = []; // Temporary selection for modal
    
    @Output() public showNewButton = new EventEmitter<boolean>();

    public searchContact = new EventEmitter<string>();

    ngOnInit(): void {
        const now = new Date(Date.now());
        this.today = now.getFullYear() + '-' + (now.getMonth() + 1).toString().padStart(2, '0') + '-' + now.getDate().toString().padStart(2, '0');
        
        const yesterdayDate = new Date(now);
        yesterdayDate.setDate(yesterdayDate.getDate() - 1);
        this.yesterday = yesterdayDate.getFullYear() + '-' + (yesterdayDate.getMonth() + 1).toString().padStart(2, '0') + '-' + yesterdayDate.getDate().toString().padStart(2, '0');
        
        this.route.paramMap.subscribe(params => {
            if (params.has('id')) {
                const chatId = parseInt(params.get('id'), 10);
                this.resolutionCenterService.getChatById(chatId).then(res => {
                    this.selectedChat = res.data;
                });
            }
        });

        this.loadUserTagLists();
        void this.loadChatList().finally(() => this.startChatListPoll());
        this.applyOpenDirectChatRequest();
    }

    ngOnDestroy(): void {
        this.stopChatListPoll();
    }

    private applyOpenDirectChatRequest(): void {
        const request = this.resolutionCenterService.openDirectChatRequest;
        if (!request) return;

        this.resolutionCenterService.openDirectChatRequest = null;

        this.resolutionCenterService.getChatByUserId(request.userId).then(({ data, error }) => {
            if (!error && data) {
                this.selectedChat = data;
            } else {
                this.userService.getUserDetails(request.userId).then(({ data: userData, error: userError }) => {
                    if (!userError && userData) {
                        this.selectedUserForNewChat = userDetailsToChatUser(userData);
                        this.selectedChat = null;
                        this.resetMessagesTabTrigger++;
                    }
                });
            }
        });
    }

    async load() {
        this.loadChatList();
    }

    async loadChatList() {
        this.isLoading = true;
        const { data, error } = await this.resolutionCenterService.getChatList(1, this.searchQuery, this.chatStatus, this.selectedTags);

        if (!error) {
            this._list = data;
            this.conversations = (data.data as unknown as ClientChat[]) || [];
            this.showLoadConversations = data.last_page > this._page;

            //messages are now loaded by messages-tab component via getCustomerMessagesById(userId)
            //no need to reload messages here when chat list is refreshed
        }

        this.isLoading = false;
    }

    async loadMoreChats() {
        this._contactPage++;
        this.showLoadMoreChats = false;
        const { data, error } = await this.resolutionCenterService.getChatList(this._contactPage, this.searchQuery, this.chatStatus, this.selectedTags);
        
        if (!error) {
            const incoming = (data.data as unknown as ClientChat[]) || [];
            const merged = [...this.conversations, ...incoming];
            this._list.data = merged;
            this.conversations = merged;
            this.showLoadConversations = this.shouldShowLoadMoreButton(data.current_page, data.last_page);
        }
    }

    onSelectedChat(chat: ClientChat) {
        if (this.selectedChat && chat.id === this.selectedChat.id) {
            return;
        }

        this.selectedUserForNewChat = null;
        chat.unread_count = 0;
        this.selectedChat = chat;
        this._page = 1;
        
        //messages are now loaded by messages-tab component via getCustomerMessagesById(userId)
        //no need to call getContactMessages here to avoid duplicate API calls
        this.resolutionCenterService.updateTotalNotifications.next(true);
    }

    setChatStatus(status: '' | 'unread' | 'archived' | 'favorited') {
        if (this.chatStatus === status) {
            return;
        }

        this.chatStatus = status;
        this._contactPage = 1;
        this._page = 1;
        this.loadChatList();
    }

    async onChatUpdated(updated: ClientChat) {
        if (!updated?.id) {
            return;
        }

        const willRefreshList = this.chatStatus === 'favorited' || this.chatStatus === 'archived';

        if (willRefreshList) {
            // Clear selection first so the panel closes immediately (no star flash).
            // Then refresh the list.
            this.selectedChat = null;
            await this.loadChatList();
            return;
        }

        // Update selected chat. If none is selected yet (e.g. opened via user search),
        // set this as the current selected chat so header and right panel update.
        if (!this.selectedChat || this.selectedChat.id === updated.id) {
            this.selectedChat = { ...(this.selectedChat || {} as any), ...updated } as any;
        }

        // Update list item
        const idx = this.conversations.findIndex(t => t.id === updated.id);
        if (idx !== -1) {
            const current = this.conversations[idx];
            const merged = { ...current, ...updated } as ClientChat;
            this.conversations = [
                ...this.conversations.slice(0, idx),
                merged,
                ...this.conversations.slice(idx + 1)
            ];
        }
    }

    onChatCreated(shouldReload: boolean) {
        if (!shouldReload) {
            return;
        }

        // Reload the chat list to get the new chat
        // This ensures the new chat appears even if filters are applied
        this._contactPage = 1;
        this._page = 1;
        this.loadChatList();
        
        // Note: The new chat will be selected automatically when it appears in the list
        // or we can wait for the list to load and then select it
    }

    onMessageSent(shouldReload: boolean) {
        if (!shouldReload) {
            return;
        }

        this._contactPage = 1;
        this._page = 1;
        this.loadChatList();
    }

    onNewChatButtonClick() {
        this.selectedUserForNewChat = null;
        // Clear selected chat to show user picker
        this.selectedChat = null;
        // Force reset by incrementing trigger (this will trigger ngOnChanges even if chat was already null)
        this.resetMessagesTabTrigger++;
    }

    getInitials(user: ChatUser): string {
        if (!user.first_name && !user.last_name) return '?';
        
        const parts = (user.first_name + ' ' + user.last_name).trim().split(/\s+/);
        return parts.length >= 2
            ? (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
            : parts[0].charAt(0).toUpperCase();
    }

    getUserName(user: ChatUser): string {
        return !user.first_name && !user.last_name
            ? '?'
            : (user.first_name + ' ' + user.last_name).trim();
    }

    getLastMessage(chat: ClientChat): string {
        return getLastMessageAttachmentPreview(chat.last_message, 'direct').text;
    }

    clearSearch() {
        this.searchQuery = '';
        this.searchQueryStream.next('');
        this._contactPage = 1;
        this._page = 1;
        this.loadChatList();
    }

    async loadUserTagLists() {
        const { data, error } = await this.userService.getUserTagLists();
        if (!error && data) {
            // Ensure tags is always an array for each tag list
            this.userTagLists = data.map(tagList => ({
                ...tagList,
                tags: Array.isArray(tagList.tags) ? tagList.tags : []
            }));
        }
    }

    openFiltersModal() {
        // Copy current selected tags to temporary selection
        this.tempSelectedTags = [...this.selectedTags];
        this.showFiltersModal = true;
    }

    closeFiltersModal() {
        this.showFiltersModal = false;
    }

    toggleTagSelection(tagId: number) {
        const index = this.tempSelectedTags.indexOf(tagId);
        if (index > -1) {
            // Deselect tag
            this.tempSelectedTags.splice(index, 1);
        } else {
            // Select tag
            this.tempSelectedTags.push(tagId);
        }
    }

    isTagSelected(tagId: number): boolean {
        return this.tempSelectedTags.includes(tagId);
    }

    applyFilters() {
        // Apply the temporary selection to the actual selected tags
        this.selectedTags = [...this.tempSelectedTags];
        this.closeFiltersModal();
        // Reload chat list with new filters
        this._contactPage = 1;
        this._page = 1;
        this.loadChatList();
    }

    cancelFilters() {
        // Reset temporary selection to current selected tags
        this.tempSelectedTags = [...this.selectedTags];
        this.closeFiltersModal();
    }

    hasUserTagLists(): boolean {
        return this.userTagLists && this.userTagLists.length > 0;
    }

    getTagById(tagId: number): UserTag | null {
        for (const tagList of this.userTagLists) {
            const tag = tagList.tags?.find(t => t.id === tagId);
            if (tag) {
                return tag;
            }
        }
        return null;
    }

    removeTag(tagId: number) {
        const index = this.selectedTags.indexOf(tagId);
        if (index > -1) {
            this.selectedTags.splice(index, 1);
            // Reload chat list with updated filters
            this._contactPage = 1;
            this._page = 1;
            this.loadChatList();
        }
    }

    clearAllFilters() {
        this.selectedTags = [];
        // Reload chat list without filters
        this._contactPage = 1;
        this._page = 1;
        this.loadChatList();
    }

    private shouldShowLoadMoreButton(currentPage: number, lastPage: number) {
        return currentPage < lastPage;
    }

    /** Light poll for list updates until WebSockets are implemented. */
    private chatListPollIntervalId: ReturnType<typeof setInterval> | null = null;
    private readonly CHAT_LIST_POLL_INTERVAL_MS = 30_000;
    private chatListPollInFlight = false;

    private startChatListPoll(): void {
        this.stopChatListPoll();
        this.chatListPollIntervalId = setInterval(() => {
            void this.refreshChatList();
        }, this.CHAT_LIST_POLL_INTERVAL_MS);
    }

    private stopChatListPoll(): void {
        if (this.chatListPollIntervalId != null) {
            clearInterval(this.chatListPollIntervalId);
            this.chatListPollIntervalId = null;
        }
    }

    /** Re-fetch loaded pages without the loading spinner so last message / unread counts stay current. */
    private async refreshChatList(): Promise<void> {
        if (this.isLoading || this.chatListPollInFlight) {
            return;
        }

        this.chatListPollInFlight = true;
        const pagesToRefresh = Math.max(1, this._contactPage);
        const selectedChatId = this.selectedChat?.id;
        const selectedUnreadCount = this.selectedChat?.unread_count ?? 0;

        try {
            const merged: ClientChat[] = [];
            let lastPage = 1;

            for (let page = 1; page <= pagesToRefresh; page++) {
                const { data, error } = await this.resolutionCenterService.getChatList(
                    page, this.searchQuery, this.chatStatus, this.selectedTags
                );
                if (error || !data) {
                    return;
                }

                merged.push(...((data.data as unknown as ClientChat[]) || []));
                lastPage = data.last_page;
            }

            const seen = new Set<number>();
            this.conversations = merged.filter(chat => {
                if (seen.has(chat.id)) {
                    return false;
                }
                seen.add(chat.id);
                return true;
            });
            this.showLoadConversations = lastPage > this._contactPage;

            if (selectedChatId) {
                const refreshed = this.conversations.find(c => c.id === selectedChatId);
                if (refreshed) {
                    this.selectedChat = { ...refreshed, unread_count: selectedUnreadCount };
                }
            }
        } finally {
            this.chatListPollInFlight = false;
        }
    }

    private _selectedChat = null;
    private _contactPage = 1;

    //mantis demo stuff

    icon_list = [
        {
            icon: 'phone'
        },
        {
            icon: 'message'
        },
        {
            icon: 'video-camera'
        }
    ];
  
    cards = [
        {
            title: 'All File',
            amount: '231',
            background: 'bg-light-primary',
            icon: 'folder-open',
            text_color: 'text-primary'
        },
        {
            title: 'All Link',
            amount: '231',
            background: 'bg-light-secondary',
            icon: 'link',
            text_color: 'text-secondary'
        }
    ];
  
    file = [
        {
            background: 'btn-light-success text-success',
            icon: 'file-done',
            title: 'Document',
            text: '123 files, 193MB'
        },
        {
            background: 'btn-light-warning text-warning',
            icon: 'picture',
            title: 'Photos',
            text: '53 files, 321MB'
        },
        {
            background: 'btn-light-primary',
            icon: 'file-sync',
            title: 'Other',
            text: '49 files, 193MB'
        }
    ];
  
    footer_icon = [
        {
            icon: 'smile'
        },
        {
            icon: 'paper-clip'
        },
        {
            icon: 'picture'
        },
        {
            icon: 'sound'
        }
    ];
}
